import React, { useState } from 'react';
import { UserPlus, X, Search, Users, Database, Activity, Edit3, User, Trash2, PauseCircle, PlayCircle, LogIn, Eye, EyeOff, Sparkles, Sliders, Box, ShieldCheck, Mail, Check, Layers, Cpu, HardDrive } from 'lucide-react';

const SystemUsersSection = ({
  showUserForm, setShowUserForm, clearUserForm, handleUserSubmit, editingUser, startEditUser, handleUserDelete,
  newUsername, setNewUsername, newFullName, setNewFullName, newEmail, setNewEmail,
  newRole, setNewRole, newSubscriptionStatus, setNewSubscriptionStatus, newPassword, setNewPassword, isSubmitting,
  accountType = 'customer', setAccountType,
  packageMode, setPackageMode, selectedPackage, setSelectedPackage, customLimitsForm, setCustomLimitsForm,
  sendWelcomeEmail, setSendWelcomeEmail, packages = [],
  userFilter, setUserFilter, userRoleFilter, setUserRoleFilter, userStatusFilter, setUserStatusFilter, userSort, setUserSort, setCurrentPage,
  paginatedUsers, filteredUsers, selectedUserIds, setSelectedUserIds, toggleAllSelection, toggleUserSelection,
  viewingUser, setViewingUser, setEditingLimitsForUser, setCustomLimits, users, userResourceUsage, setSelectedUserResourceDetails, setActiveSection,
  api, superAdminApi, showStatus, loadAll, user, formatBytesValue,
  setSelectedTeamMember, setSelectedTeamMemberParent, setSelectedAdminForMembers
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const generateRandomPassword = () => {
    const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lowercase = 'abcdefghijkmnpqrstuvwxyz';
    const numbers = '23456789';
    const symbols = '!@#$%^&*';
    const all = uppercase + lowercase + numbers + symbols;
    let pwd = '';
    pwd += uppercase[Math.floor(Math.random() * uppercase.length)];
    pwd += lowercase[Math.floor(Math.random() * lowercase.length)];
    pwd += numbers[Math.floor(Math.random() * numbers.length)];
    pwd += symbols[Math.floor(Math.random() * symbols.length)];
    for (let i = 4; i < 16; i++) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }
    pwd = pwd.split('').sort(() => 0.5 - Math.random()).join('');
    setNewPassword(pwd);
    setShowPassword(true);
  };

  const handleCustomLimitChange = (field, val) => {
    if (setCustomLimitsForm) {
      setCustomLimitsForm(prev => ({
        ...prev,
        [field]: val
      }));
    }
  };

  // Find currently selected package object for preview
  const currentPkg = (packages || []).find(p => (p.tierId === selectedPackage || p.id === selectedPackage)) || {
    tier: selectedPackage,
    limits: {}
  };

  return (
    <div className="sa-section fade-in">
      <div className="dashboard-header">
        <div className="dashboard-header-title-area">
          <h2>User Management</h2>
        </div>
        <button className="action-btn-premium primary" onClick={() => { clearUserForm(); setShowUserForm(true); }}>
          <UserPlus size={14} /> New User
        </button>
      </div>

      {/* Modern Viewport-Locked Modal / Slide-Over Dialog */}
      {showUserForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(15, 23, 42, 0.72)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.25rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSubmitting) clearUserForm();
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '20px',
              maxWidth: '720px',
              width: '100%',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.6), 0 0 0 1px var(--border)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1.25rem 1.75rem',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'var(--surface)',
                flexShrink: 0
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(var(--primary-rgb), 0.12)',
                    color: 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <UserPlus size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
                    {editingUser ? `Edit User: ${editingUser.username}` : 'Create New System User'}
                  </h3>
                  <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {editingUser
                      ? 'Update account credentials, permissions, and tailor plan or custom enterprise quotas.'
                      : 'Provision user credentials, assign catalog packages, or configure dedicated custom quotas.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={clearUserForm}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <form
              onSubmit={handleUserSubmit}
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  padding: '1.75rem',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem',
                  flex: 1
                }}
              >
                {/* SECTION 0: Account Classification */}
                <div
                  style={{
                    background: 'var(--surface-2, rgba(255,255,255,0.02))',
                    border: '1px solid var(--border)',
                    borderRadius: '14px',
                    padding: '1.25rem'
                  }}
                >
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.65rem', letterSpacing: '0.3px' }}>
                    ACCOUNT CLASSIFICATION <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem' }}>
                    {/* Customer Account Button */}
                    <button
                      type="button"
                      onClick={() => setAccountType && setAccountType('customer')}
                      style={{
                        padding: '0.9rem 1.1rem',
                        borderRadius: '12px',
                        border: accountType === 'customer' ? '2px solid var(--primary)' : '1px solid var(--border)',
                        background: accountType === 'customer' ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--surface)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.85rem',
                        boxShadow: accountType === 'customer' ? '0 4px 14px rgba(var(--primary-rgb), 0.15)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: accountType === 'customer' ? 'var(--primary)' : 'var(--surface-2)',
                          color: accountType === 'customer' ? '#ffffff' : 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <User size={18} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                          Customer Account
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.2rem', lineHeight: 1.35 }}>
                          Tenant owner (Admin) or individual solo developer with subscription quotas
                        </div>
                      </div>
                    </button>

                    {/* Platform Staff Button */}
                    <button
                      type="button"
                      onClick={() => setAccountType && setAccountType('staff')}
                      style={{
                        padding: '0.9rem 1.1rem',
                        borderRadius: '12px',
                        border: accountType === 'staff' ? '2px solid #8b5cf6' : '1px solid var(--border)',
                        background: accountType === 'staff' ? 'rgba(139, 92, 246, 0.08)' : 'var(--surface)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.85rem',
                        boxShadow: accountType === 'staff' ? '0 4px 14px rgba(139, 92, 246, 0.2)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: accountType === 'staff' ? '#8b5cf6' : 'var(--surface-2)',
                          color: accountType === 'staff' ? '#ffffff' : 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <ShieldCheck size={18} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                          Platform Staff
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.2rem', lineHeight: 1.35 }}>
                          Super Admin with root system access and unrestricted cluster authority
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* SECTION 1: Identity & Credentials */}
                <div
                  style={{
                    background: 'var(--surface-2, rgba(255,255,255,0.02))',
                    border: '1px solid var(--border)',
                    borderRadius: '14px',
                    padding: '1.25rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                    <User size={16} color="var(--primary)" />
                    <span>Account Credentials & Profile</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
                    {/* Full Name */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                        Full Name
                      </label>
                      <input
                        type="text"
                        className="premium-input"
                        placeholder="e.g. Sarah Ahmed"
                        value={newFullName}
                        onChange={e => setNewFullName && setNewFullName(e.target.value)}
                        style={{ width: '100%', margin: 0 }}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                        Email Address <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="email"
                        className="premium-input"
                        placeholder="sarah@company.com"
                        required
                        value={newEmail}
                        onChange={e => {
                          const val = e.target.value;
                          if (setNewEmail) setNewEmail(val);
                          // Auto suggest username if creating user and username is empty
                          if (!editingUser && (!newUsername || newUsername.trim() === '')) {
                            const suggested = val.split('@')[0].replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
                            if (setNewUsername) setNewUsername(suggested);
                          }
                        }}
                        style={{ width: '100%', margin: 0 }}
                      />
                    </div>

                    {/* Username */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                        Username <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="premium-input"
                        placeholder="e.g. sarah_admin"
                        required
                        value={newUsername}
                        onChange={e => setNewUsername && setNewUsername(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                        style={{
                          width: '100%',
                          margin: 0,
                          borderColor: (users && users.some(u => u.username?.toLowerCase() === (newUsername || '').trim().toLowerCase() && (!editingUser || u.username !== editingUser.username))) ? '#ef4444' : undefined
                        }}
                      />
                      {users && users.some(u => u.username?.toLowerCase() === (newUsername || '').trim().toLowerCase() && (!editingUser || u.username !== editingUser.username)) && (
                        <span style={{ color: '#ef4444', fontSize: '0.74rem', marginTop: '0.25rem', display: 'block', fontWeight: 600 }}>
                          ⚠️ Username is already taken. Please choose a different username.
                        </span>
                      )}
                    </div>

                    {/* Assigned System Role (Auto-synced & Read-only) */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                        Assigned System Role
                      </label>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          padding: '0.62rem 0.85rem',
                          borderRadius: '8px',
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: accountType === 'staff' ? '#8b5cf6' : (newRole === 'admin' ? 'var(--primary)' : '#10b981')
                        }}
                      >
                        <ShieldCheck size={16} color={accountType === 'staff' ? '#8b5cf6' : (newRole === 'admin' ? 'var(--primary)' : '#10b981')} />
                        <span>
                          {accountType === 'staff'
                            ? 'Super Admin (Platform Staff)'
                            : (newRole === 'admin' ? 'Admin (Tenant Owner)' : 'Developer (Standard User)')}
                        </span>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '12px',
                            background: accountType === 'staff' ? 'rgba(139, 92, 246, 0.12)' : 'rgba(var(--primary-rgb), 0.1)',
                            color: accountType === 'staff' ? '#8b5cf6' : 'var(--primary)',
                            marginLeft: 'auto',
                            fontWeight: 600
                          }}
                        >
                          {accountType === 'staff' ? 'Root Staff' : 'Auto-synced'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Password with Generator */}
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                        Password {editingUser ? '(Leave empty to keep current password)' : <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      <button
                        type="button"
                        onClick={generateRandomPassword}
                        style={{
                          background: 'rgba(var(--primary-rgb), 0.12)',
                          border: '1px solid rgba(var(--primary-rgb), 0.25)',
                          borderRadius: '6px',
                          color: 'var(--primary)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          padding: '0.2rem 0.6rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Sparkles size={12} /> Generate Password
                      </button>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="premium-input"
                        placeholder={editingUser ? '••••••••' : 'Min 8 chars (upper, lower, num, special)'}
                        value={newPassword}
                        onChange={e => setNewPassword && setNewPassword(e.target.value)}
                        required={!editingUser}
                        style={{ width: '100%', margin: 0, paddingRight: '2.5rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: 0
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Plan Selection OR Platform Staff Privileges Card */}
                {accountType === 'staff' ? (
                  <div
                    style={{
                      background: 'rgba(139, 92, 246, 0.05)',
                      border: '1px solid rgba(139, 92, 246, 0.25)',
                      borderRadius: '14px',
                      padding: '1.25rem',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      animation: 'fadeIn 0.2s ease-out'
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'rgba(139, 92, 246, 0.15)',
                        color: '#8b5cf6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <ShieldCheck size={22} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-main)', marginBottom: '0.25rem' }}>
                        Platform Staff Privileges Active
                      </div>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        Super Admin staff accounts possess unrestricted access across all tenants, cloud container runtimes, node hosts, and audit trails. Subscription quotas and package billing are not applicable to platform staff.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      background: 'var(--surface-2, rgba(255,255,255,0.02))',
                      border: '1px solid var(--border)',
                      borderRadius: '14px',
                      padding: '1.25rem',
                      animation: 'fadeIn 0.2s ease-out'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                        <Layers size={16} color="var(--primary)" />
                        <span>Plan & Resource Allocation</span>
                      </div>

                      {/* Segmented Mode Selector */}
                      <div
                        style={{
                          display: 'flex',
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          padding: '2px'
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setPackageMode && setPackageMode('package')}
                          style={{
                            padding: '0.35rem 0.85rem',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: packageMode === 'package' ? 'var(--primary)' : 'transparent',
                            color: packageMode === 'package' ? '#ffffff' : 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Box size={13} /> Platform Package
                        </button>
                        <button
                          type="button"
                          onClick={() => setPackageMode && setPackageMode('custom')}
                          style={{
                            padding: '0.35rem 0.85rem',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: packageMode === 'custom' ? 'var(--primary)' : 'transparent',
                            color: packageMode === 'custom' ? '#ffffff' : 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Sliders size={13} /> Custom Plan
                        </button>
                      </div>
                    </div>

                    {/* Mode A: Platform Package Dropdown (100% Dynamic from DB) */}
                    {packageMode === 'package' && (
                      <div>
                        <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                          Select Package Tier
                        </label>
                        <select
                          className="premium-input"
                          value={selectedPackage}
                          onChange={e => setSelectedPackage && setSelectedPackage(e.target.value)}
                          style={{ width: '100%', margin: 0 }}
                        >
                          {(packages && packages.length > 0) ? (
                            packages.map(p => {
                              const tId = p.tierId || p.id;
                              const priceStr = (p.price === 'Custom' || p.price === 'custom') ? 'Custom Pricing' : (p.price ? `${p.price}/mo` : 'Free');
                              const members = p.limits?.maxTeamMembers ?? (tId === 'hobby' ? 0 : 5);
                              const memberRole = members > 0 ? `${members} Members • Admin` : 'Solo • Developer';
                              return (
                                <option key={tId} value={tId}>
                                  {p.tier || tId} — {priceStr} ({memberRole})
                                </option>
                              );
                            })
                          ) : (
                            <>
                              <option value="hobby">Hobby — Free (Solo • Developer)</option>
                              <option value="pro">Pro Agency — $29/mo (5 Members • Admin)</option>
                              <option value="enterprise">Enterprise — $199/mo (Unlimited Members • Admin)</option>
                            </>
                          )}
                        </select>

                        {/* Package Quota Preview Card */}
                        <div
                          style={{
                            marginTop: '0.85rem',
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            fontSize: '0.8rem',
                            color: 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1.25rem',
                            flexWrap: 'wrap'
                          }}
                        >
                          <div>Deployments: <strong style={{ color: 'var(--text-main)' }}>{currentPkg.limits?.maxDeployments ?? (selectedPackage === 'hobby' ? 3 : 25)}</strong></div>
                          <div>Team Members: <strong style={{ color: 'var(--text-main)' }}>{currentPkg.limits?.maxTeamMembers ?? (selectedPackage === 'hobby' ? 0 : 5)}</strong></div>
                          <div>Storage: <strong style={{ color: 'var(--text-main)' }}>{currentPkg.limits?.maxStorageGB ?? (selectedPackage === 'hobby' ? 5 : 20)} GB</strong></div>
                          <div>Assigned Role: <span style={{ color: (currentPkg.limits?.maxTeamMembers > 0 || selectedPackage !== 'hobby') ? 'var(--primary)' : '#10b981', fontWeight: 700 }}>
                            {(currentPkg.limits?.maxTeamMembers > 0 || selectedPackage !== 'hobby') ? 'Admin (Tenant Owner)' : 'Developer (Standard)'}
                          </span></div>
                        </div>
                      </div>
                    )}

                    {/* Mode B: Custom Plan Quotas */}
                    {packageMode === 'custom' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', animation: 'fadeIn 0.2s ease-out' }}>
                        {/* Custom Plan Label */}
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                            Custom Plan Label / Name
                          </label>
                          <input
                            type="text"
                            className="premium-input"
                            placeholder="e.g. Enterprise Custom Plan (Client XYZ)"
                            value={customLimitsForm?.planName || ''}
                            onChange={e => handleCustomLimitChange('planName', e.target.value)}
                            style={{ width: '100%', margin: 0 }}
                          />
                        </div>

                        {/* Quota inputs grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.85rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                              Max Deployments
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="500"
                              className="premium-input"
                              value={customLimitsForm?.maxDeployments ?? 25}
                              onChange={e => handleCustomLimitChange('maxDeployments', e.target.value)}
                              style={{ width: '100%', margin: 0 }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                              Max Team Members <span style={{ fontSize: '0.7rem', color: 'var(--primary)' }}>({Number(customLimitsForm?.maxTeamMembers) > 0 ? 'Admin' : 'Developer'})</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              className="premium-input"
                              value={customLimitsForm?.maxTeamMembers ?? 5}
                              onChange={e => handleCustomLimitChange('maxTeamMembers', e.target.value)}
                              style={{ width: '100%', margin: 0 }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                              Total RAM (GB)
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="128"
                              className="premium-input"
                              value={customLimitsForm?.memoryLimitGB ?? 8}
                              onChange={e => handleCustomLimitChange('memoryLimitGB', e.target.value)}
                              style={{ width: '100%', margin: 0 }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                              Total CPU (Cores)
                            </label>
                            <input
                              type="number"
                              step="0.5"
                              min="0.5"
                              max="64"
                              className="premium-input"
                              value={customLimitsForm?.cpuLimit ?? 4}
                              onChange={e => handleCustomLimitChange('cpuLimit', e.target.value)}
                              style={{ width: '100%', margin: 0 }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                              Storage (GB)
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="1000"
                              className="premium-input"
                              value={customLimitsForm?.storageLimitGB ?? 20}
                              onChange={e => handleCustomLimitChange('storageLimitGB', e.target.value)}
                              style={{ width: '100%', margin: 0 }}
                            />
                          </div>
                        </div>

                        {/* Custom Feature Toggles */}
                        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', paddingTop: '0.5rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-main)' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(customLimitsForm?.cicdEnabled)}
                              onChange={e => handleCustomLimitChange('cicdEnabled', e.target.checked)}
                            />
                            <span>Enable CI/CD Pipelines</span>
                          </label>

                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-main)' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(customLimitsForm?.backupsEnabled)}
                              onChange={e => handleCustomLimitChange('backupsEnabled', e.target.checked)}
                            />
                            <span>Enable Automated Backups</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* SECTION 3: Status & Communication */}
                <div
                  style={{
                    background: 'var(--surface-2, rgba(255,255,255,0.02))',
                    border: '1px solid var(--border)',
                    borderRadius: '14px',
                    padding: '1.25rem'
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', alignItems: 'center' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                        Subscription Status
                      </label>
                      <select
                        className="premium-input"
                        value={newSubscriptionStatus}
                        onChange={e => setNewSubscriptionStatus && setNewSubscriptionStatus(e.target.value)}
                        style={{ width: '100%', margin: 0 }}
                      >
                        <option value="active">Active (Granted Full Access)</option>
                        <option value="suspended">Suspended (Locked)</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {!editingUser && (
                      <div style={{ paddingTop: '1.2rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-main)' }}>
                          <input
                            type="checkbox"
                            checked={Boolean(sendWelcomeEmail)}
                            onChange={e => setSendWelcomeEmail && setSendWelcomeEmail(e.target.checked)}
                          />
                          <span style={{ fontWeight: 600 }}>Send Welcome Email with credentials</span>
                        </label>
                        <p style={{ margin: '0.2rem 0 0 1.6rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          Sends temporary login password and allocated resources breakdown to the user.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Sticky Footer */}
              <div
                style={{
                  padding: '1.1rem 1.75rem',
                  borderTop: '1px solid var(--border)',
                  background: 'var(--surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '0.85rem',
                  flexShrink: 0
                }}
              >
                <button
                  type="button"
                  onClick={clearUserForm}
                  style={{
                    padding: '0.65rem 1.25rem',
                    borderRadius: '10px',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-main)',
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '0.65rem 1.5rem',
                    borderRadius: '10px',
                    background: isSubmitting ? 'var(--border)' : 'var(--primary)',
                    border: 'none',
                    color: isSubmitting ? 'var(--text-muted)' : '#ffffff',
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(var(--primary-rgb), 0.3)',
                    transition: 'all 0.2s'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="deploy-spin" style={{ width: '14px', height: '14px', border: '2px solid var(--text-muted)', borderTopColor: 'transparent', borderRadius: '50%' }} />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={16} />
                      <span>{editingUser ? 'Save User Changes' : 'Create User & Provision Plan'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Controls Toolbar */}
      <div style={{ margin: '0 1.5rem 1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div className="dashboard-search-wrapper" style={{ flex: 1, minWidth: '250px', margin: 0 }}>
          <div className="dashboard-search-icon-box"><Search size={18} /></div>
          <input type="text" className="dashboard-search-input" placeholder="Search users..." value={userFilter} onChange={e => { setUserFilter(e.target.value); setCurrentPage(1); }} />
        </div>
        <select className="premium-input" style={{ width: 'auto', margin: 0 }} value={userRoleFilter} onChange={e => { setUserRoleFilter(e.target.value); setCurrentPage(1); }}>
          <option value="all">All Roles</option>
          <option value="admin">Admins</option>
          <option value="developer">Developers</option>
          <option value="superadmin">Super Admins</option>
        </select>
        <select className="premium-input" style={{ width: 'auto', margin: 0 }} value={userStatusFilter} onChange={e => { setUserStatusFilter(e.target.value); setCurrentPage(1); }}>
          <option value="all">All Active Users</option>
          <option value="active">Active & Paid</option>
          <option value="payment_failed">Payment Failed</option>
          <option value="suspended">Suspended</option>
          <option value="deleted">Deleted Users (Archived)</option>
        </select>
        <select className="premium-input" style={{ width: 'auto', margin: 0 }} value={userSort} onChange={e => { setUserSort(e.target.value); setCurrentPage(1); }}>
          <option value="username">Sort: A-Z</option>
          <option value="newest">Sort: Newest</option>
          <option value="lastLogin">Sort: Last Login</option>
        </select>
      </div>

      {selectedUserIds.size > 0 && (
        <div style={{ margin: '0 1.5rem 1.5rem', padding: '0.75rem 1.5rem', background: 'var(--surface-hover)', border: '1px solid var(--primary)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: 'var(--text-main)', fontWeight: 600 }}>{selectedUserIds.size} users selected</div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button className="sa-btn-danger" onClick={async () => {
              if (window.confirm(`Bulk suspend ${selectedUserIds.size} users?`)) {
                try {
                  const usernamesToSuspend = Array.from(selectedUserIds);
                  await superAdminApi.bulkSuspendUsers(usernamesToSuspend);
                  showStatus(`Successfully suspended ${usernamesToSuspend.length} users.`, 'success');
                  setSelectedUserIds(new Set());
                  if (typeof loadAll === 'function') loadAll();
                } catch (err) {
                  showStatus(`Bulk suspend failed: ${err.message}`, 'error');
                }
              }
            }}>Bulk Suspend</button>
            <button className="sa-action-btn" style={{ padding: '0.25rem 1rem' }} onClick={() => setSelectedUserIds(new Set())}>Cancel</button>
          </div>
        </div>
      )}

      {viewingUser && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '400px', background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 9999, padding: '2rem', paddingTop: '6rem', boxShadow: '-10px 0 20px rgba(0,0,0,0.1)', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>User Profile</h2>
            <button className="active-deployment-dismiss" onClick={() => setViewingUser(null)}><X size={20} /></button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
            <div className="sa-org-avatar" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', width: '64px', height: '64px', fontSize: '1.5rem' }}>
              {viewingUser?.username?.substring(0, 2).toUpperCase() || 'U'}
            </div>
            <div>
              <h3 style={{ margin: 0, color: 'var(--text-main)' }}>{viewingUser.username}</h3>
              <div style={{ color: 'var(--text-muted)' }}>{viewingUser.email || 'No email'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Status & Role</div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <span className="sa-role-badge sa-badge-admin">{viewingUser.role}</span>
                <span style={{ padding: '0.2rem 0.6rem', borderRadius: '4px', background: viewingUser.emailVerified ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: viewingUser.emailVerified ? '#10b981' : '#f59e0b', fontSize: '0.8rem', fontWeight: 600 }}>
                  {viewingUser.emailVerified ? 'Email Verified' : 'Unverified'}
                </span>
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Resource Usage</div>
              <div style={{ background: 'var(--bg-main)', padding: '1rem', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)' }}>{userResourceUsage?.find(u => u.username === viewingUser.username)?.activeApps || 0}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Active Apps</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)' }}>{userResourceUsage?.find(u => u.username === viewingUser.username)?.totalDeployments || 0}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Total Deployments</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)' }}>{userResourceUsage?.find(u => u.username === viewingUser.username)?.cpuPercent?.toFixed(1) || '0.0'}%</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>CPU</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-main)' }}>{formatBytesValue(userResourceUsage?.find(u => u.username === viewingUser.username)?.memUsageBytes || 0)}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>RAM</div>
                </div>
              </div>
            </div>

            {viewingUser.role === 'admin' && (
              <div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Team Members</div>
                <div style={{ background: 'var(--bg-main)', padding: '1rem', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(() => {
                    const members = viewingUser.teamMembers || users.filter(u => u.createdBy === viewingUser.username || u.owner === viewingUser.username || u.parentAdmin === viewingUser.username);
                    if (members.length === 0) {
                      return <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No team members found.</div>;
                    }
                    return members.map(member => (
                      <div key={member.username} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'var(--surface)', borderRadius: '4px', border: '1px solid var(--border)' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.9rem' }}>{member.username}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Role: {member.role}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: member.isActive !== false ? '#22c55e' : '#ef4444' }}></div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{member.isActive !== false ? 'Active' : 'Suspended'}</span>
                          </div>
                          <button
                            className="sa-action-btn"
                            style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', color: '#8b5cf6', borderColor: '#8b5cf6' }}
                            onClick={() => {
                              setViewingUser(null);
                              setSelectedTeamMember(member);
                              setSelectedTeamMemberParent(viewingUser);
                              setActiveSection('team_member_details');
                            }}
                            title="View Resource Allocations"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="sa-table-wrapper">
        <table className="sa-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}><input type="checkbox" onChange={toggleAllSelection} checked={paginatedUsers.length > 0 && selectedUserIds.size === paginatedUsers.length} /></th>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Verification</th>
              <th>Active apps</th>
              <th>Last Login</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
              <th style={{ textAlign: 'center' }}>Manage Resources</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="9" className="sa-empty-cell">
                  <Users size={32} color="var(--text-tertiary)" style={{ margin: '0 auto 1rem' }} />
                  <div>No users found matching your criteria.</div>
                </td>
              </tr>
            ) : (
              paginatedUsers.map(u => {
                const isSelf = u.username === user?.username;
                const isDeleted = Boolean(u.deletedAt);
                const isPaymentFailed = !isDeleted && (u.subscriptionStatus === 'unpaid' || u.subscriptionStatus === 'past_due' || u.subscriptionStatus === 'failing');
                const isSuspendedByAdmin = !isDeleted && (u.subscriptionStatus === 'suspended' || (!u.isActive && !isPaymentFailed));
                const status = isDeleted ? 'deleted' : ((!u.isActive || u.subscriptionStatus === 'suspended') ? 'suspended' : 'active');
                const lastLogin = isSelf ? 'Just now' : '2 hours ago';

                return (
                  <tr key={u.username} className={isSelf ? 'sa-row-highlight' : ''}>
                    <td><input type="checkbox" checked={selectedUserIds.has(u.username)} onChange={() => toggleUserSelection(u.username)} /></td>
                    <td>
                      <div className="sa-cell-with-icon">
                        <div className="sa-org-avatar" style={{
                          background: isDeleted ? 'linear-gradient(135deg, #64748b, #475569)' :
                            (u.role === 'developer' ? 'linear-gradient(135deg, #10b981, #059669)' :
                            (u.role === 'superadmin' ? 'linear-gradient(135deg, #ef4444, #b91c1c)' :
                              'linear-gradient(135deg, #3b82f6, #2563eb)'))
                        }}>
                          {u.username.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="sa-cell-bold" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {u.fullName ? (
                              <span>{u.fullName} <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 400 }}>({u.username})</span></span>
                            ) : (
                              u.username
                            )}
                            {isSelf && <span className="sa-you-badge">You</span>}
                            {isDeleted && <span style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>Deleted (Archived)</span>}
                            {isPaymentFailed && <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>Payment Failed</span>}
                            {isSuspendedByAdmin && <span style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>Suspended by SuperAdmin</span>}
                          </div>
                          <div className="sa-cell-muted">{u.email || 'No email'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`sa-role-badge sa-badge-${u.role === 'developer' ? 'dev' : 'admin'}`}
                        style={u.role === 'superadmin' ? { background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' } : {}}>
                        {u.role === 'superadmin' ? 'Super Admin' : u.role}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: status === 'active' ? '#22c55e' : (status === 'deleted' ? '#ef4444' : '#f59e0b'), boxShadow: status === 'active' ? '0 0 8px rgba(34, 197, 94, 0.5)' : 'none' }}></div>
                        <span style={{ color: 'var(--text-main)', fontSize: '0.8rem', textTransform: 'capitalize', fontWeight: 500 }}>{status}</span>
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const vStatus = u.verificationStatus || (u.emailVerified ? 'Verified' : 'Unverified');
                        const isVerified = vStatus === 'Verified';
                        const isUnverified = vStatus === 'Unverified';

                        const bg = isVerified
                          ? 'rgba(34, 197, 94, 0.1)'
                          : isUnverified
                          ? 'rgba(245, 158, 11, 0.15)'
                          : 'rgba(148, 163, 184, 0.15)';

                        const color = isVerified
                          ? '#22c55e'
                          : isUnverified
                          ? '#f59e0b'
                          : '#94a3b8';

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: bg,
                                color: color
                              }}
                            >
                              {vStatus}
                            </span>
                            {Array.isArray(u.connectedProviders) && u.connectedProviders.length > 0 && (
                              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                {u.connectedProviders.map(p => (
                                  <span
                                    key={p}
                                    style={{
                                      fontSize: '0.68rem',
                                      padding: '0.05rem 0.35rem',
                                      borderRadius: '4px',
                                      background: 'rgba(59, 130, 246, 0.08)',
                                      color: '#2563eb',
                                      border: '1px solid rgba(59, 130, 246, 0.2)'
                                    }}
                                  >
                                    Git: {p}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="sa-cell-muted" style={{ fontWeight: 500 }}>
                      {`${u.activeAppsCount ?? 0} apps`}
                    </td>
                    <td className="sa-cell-muted" style={{ fontWeight: 500 }}>{lastLogin}</td>
                    <td className="sa-actions-cell" style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button 
                        className="sa-action-btn sa-action-allocations" 
                        onClick={() => { setSelectedUserResourceDetails(u); setActiveSection('user_allocations'); }}
                        title="View Resource Allocations"
                      >
                        <Database size={13} />
                        <span>Allocations</span>
                      </button>
                      <button 
                        className="sa-action-btn sa-action-profile" 
                        onClick={() => { setViewingUser(u); setCustomLimits(u.customLimits || { maxDeployments: '', memoryLimitMB: '', storageLimitGB: '' }); }}
                        title="View Profile & Limits"
                      >
                        <User size={13} />
                        <span>Profile</span>
                      </button>
                      {u.role === 'admin' && (
                        <button 
                          className="sa-action-btn sa-action-members" 
                          onClick={() => { setSelectedAdminForMembers(u); setActiveSection('admin_members_list'); }}
                          title="View Admin Team Members"
                        >
                          <Users size={13} />
                          <span>Members</span>
                        </button>
                      )}
                      <button 
                        className="sa-action-btn sa-action-edit" 
                        onClick={() => startEditUser(u)}
                        title="Edit User"
                      >
                        <Edit3 size={13} />
                        <span>Edit</span>
                      </button>
                      {!isSelf && !isDeleted && (
                        <button
                          className="sa-action-btn sa-action-impersonate"
                          onClick={async () => {
                            if (!window.confirm(`Impersonate user "${u.username}"?\nYou will be logged out of your superadmin account and logged in as this user.`)) return;
                            try {
                              const res = await api.post(`/super-admin/users/${u.username}/impersonate`);
                              if (res.token) {
                                localStorage.setItem('token', res.token);
                                localStorage.setItem('user', JSON.stringify(res.user));
                                localStorage.setItem('impersonating', JSON.stringify({ from: user?.username }));
                                window.location.href = '/dashboard';
                              } else {
                                showStatus('Impersonation failed: no token returned', 'error');
                              }
                            } catch (err) {
                              showStatus(`Impersonation failed: ${err.message}`, 'error');
                            }
                          }}
                          title="Impersonate User"
                        >
                          <LogIn size={13} />
                          <span>Impersonate</span>
                        </button>
                      )}
                      {!isSelf && isDeleted && (
                        <>
                          <button
                            className="sa-action-btn sa-action-activate"
                            style={{ color: '#22c55e', borderColor: '#22c55e' }}
                            onClick={async () => {
                              try {
                                await api.post(`/super-admin/users/${u.username}/activate`);
                                showStatus(`Restored user ${u.username}`, 'success');
                                loadAll();
                              } catch (err) {
                                showStatus(`Failed to restore: ${err.message}`, 'error');
                              }
                            }}
                            title="Restore Deleted User"
                          >
                            <PlayCircle size={13} />
                            <span>Restore</span>
                          </button>
                          <button
                            className="sa-action-btn sa-action-delete"
                            onClick={() => handleUserDelete(u, 'hard')}
                            title="Permanently Purge User Record"
                          >
                            <Trash2 size={13} />
                            <span>Purge</span>
                          </button>
                        </>
                      )}
                      {!isSelf && !isDeleted && status !== 'suspended' && (
                        <button
                          className="sa-action-btn sa-action-suspend"
                          onClick={async () => {
                            try {
                              await api.post(`/super-admin/users/${u.username}/suspend`);
                              showStatus(`Suspended user ${u.username}`, 'success');
                              loadAll();
                            } catch (err) {
                              showStatus(`Failed to suspend: ${err.message}`, 'error');
                            }
                          }}
                          title="Suspend User & Stop Containers"
                        >
                          <PauseCircle size={13} />
                          <span>Suspend</span>
                        </button>
                      )}
                      {!isSelf && !isDeleted && status === 'suspended' && (
                        <button
                          className="sa-action-btn sa-action-activate"
                          style={{ color: '#22c55e', borderColor: '#22c55e' }}
                          onClick={async () => {
                            try {
                              await api.post(`/super-admin/users/${u.username}/activate`);
                              showStatus(`Activated user ${u.username}`, 'success');
                              loadAll();
                            } catch (err) {
                              showStatus(`Failed to activate: ${err.message}`, 'error');
                            }
                          }}
                          title="Activate User"
                        >
                          <PlayCircle size={13} />
                          <span>Activate</span>
                        </button>
                      )}
                      {!isSelf && !isDeleted && (
                        <button
                          className="sa-action-btn sa-action-delete"
                          onClick={() => handleUserDelete(u, 'soft')}
                          disabled={isSelf}
                          title="Delete User"
                        >
                          <Trash2 size={13} />
                          <span>Delete</span>
                        </button>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        className="sa-premium-btn-secondary"
                        style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }}
                        onClick={() => {
                          if (setEditingLimitsForUser) {
                            setEditingLimitsForUser(u);
                          } else if (setViewingUser) {
                            setViewingUser(u);
                          }
                          if (setCustomLimits) {
                            setCustomLimits(u.customLimits || { maxDeployments: '', memoryLimitMB: '', storageLimitGB: '', cpuLimit: '', maxDomains: '', maxDatabases: '', bandwidthLimitGB: '' });
                          }
                        }}
                      >
                        Manage Resources
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SystemUsersSection;
